/**
 * The quick-add screen's words and sums, as plain functions.
 *
 * ── WHAT LIVES HERE AND WHAT DOES NOT ──
 * The screen is four steps and one sentence at the end: "You spent ₹450 on
 * 🍽️ Meals from Petty Cash today. Save?". That sentence is the whole point of
 * the confirmation - a ten-year-old reads it and knows whether it is right -
 * so it is built here, where a Node test can read it too, rather than in JSX
 * where the only test is a screenshot.
 *
 * No money arithmetic: the amount arrives already formatted by `Money`'s own
 * formatter in the component, and the wire value goes through `@fint/money`'s
 * `parse`/`format` there as well. This file only decides what to SAY, and
 * whether what was typed is a number at all.
 */

export type QuickAddKind = 'spent' | 'got';

export interface QuickAddFacts {
  kind: QuickAddKind;
  /** Already formatted for a person: "₹450.00". */
  amountText: string;
  categoryName: string;
  categoryEmoji: string | null;
  accountName: string;
  /** `YYYY-MM-DD`, the date on the record. */
  date: string;
  /** `YYYY-MM-DD`, the org's today - see `useToday`. */
  today: string;
}

/** `2026-09-12` -> `12-09-2026`, kept here so the sentence needs no import. */
function dayMonthYear(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  return `${match[3]}-${match[2]}-${match[1]}`;
}

/** The calendar day before `date`, in the API's shape, without a timezone in sight. */
export function dayBefore(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const [, y, m, d] = match;
  const utc = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d) - 1));
  return `${utc.getUTCFullYear()}-${String(utc.getUTCMonth() + 1).padStart(2, '0')}-${String(utc.getUTCDate()).padStart(2, '0')}`;
}

/**
 * "today", "yesterday", or "on 12-09-2026".
 *
 * Relative words for the two days the chips offer, because that is what the
 * person pressed; the date itself for anything else, because "three days ago"
 * is a sum the reader has to do backwards to check.
 */
export function describeDay(date: string, today: string): string {
  if (date === today) return 'today';
  if (date === dayBefore(today)) return 'yesterday';
  return `on ${dayMonthYear(date)}`;
}

/** The category as a person says it: emoji, then name, or the name alone. */
export function categoryLabel(name: string, emoji: string | null | undefined): string {
  return emoji ? `${emoji} ${name}` : name;
}

/**
 * The confirmation sentence.
 *
 *   You spent ₹450.00 on 🍽️ Meals and entertainment from Petty Cash today.
 *   You got ₹5,000.00 as 🛠️ Services into HDFC Current yesterday.
 *
 * "from" for money leaving an account and "into" for money arriving, because
 * the preposition is the one word that says which way the money went, and a
 * person who reads it wrong at this step is exactly who this screen is for.
 */
export function confirmationSentence(facts: QuickAddFacts): string {
  const category = categoryLabel(facts.categoryName, facts.categoryEmoji);
  const day = describeDay(facts.date, facts.today);
  return facts.kind === 'spent'
    ? `You spent ${facts.amountText} on ${category} from ${facts.accountName} ${day}.`
    : `You got ${facts.amountText} as ${category} into ${facts.accountName} ${day}.`;
}

/**
 * What was typed, as the decimal string the API takes, or null when it is not
 * a positive amount.
 *
 * Tolerant of what a thumb produces on a phone keypad: spaces, a comma
 * grouping, a leading ₹ pasted from somewhere, a trailing dot. Refuses zero,
 * a negative, and anything with letters in it - the screen has no field for
 * "what went wrong", so the rule has to be "you cannot press Next until it is
 * a number".
 */
export function normaliseAmount(raw: string): string | null {
  const cleaned = raw.replace(/[₹$€£,\s]/g, '');
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0) return null;
  return cleaned.endsWith('.') ? cleaned.slice(0, -1) : cleaned;
}
